import api from './api';

export const b2bService = {
  // ----------------------------------------------------
  // 1. Governance & Party Contracts (REQ-B2B-01, 02)
  // ----------------------------------------------------
  getEligibleParties: async (centreId = null) => {
    const params = centreId ? { centreId } : {};
    const response = await api.get('/b2b/parties/eligible', { params });
    return response.data || [];
  },

  validateEligibility: async (partyId, bookingDate = null) => {
    const params = bookingDate ? { bookingDate } : {};
    const response = await api.get(`/b2b/parties/${partyId}/eligibility`, { params });
    return response.data;
  },

  getPartyContract: async (partyId, transactionDate = null) => {
    const params = transactionDate ? { transactionDate } : {};
    const response = await api.get(`/b2b/parties/${partyId}/contract`, { params });
    return response.data;
  },

  updatePartyLifecycle: async (partyId, data) => {
    const response = await api.put(`/b2b/parties/${partyId}/lifecycle`, data);
    return response.data;
  },

  // ----------------------------------------------------
  // 2. Billing & Credit Policy (REQ-B2B-03, 04, 05)
  // ----------------------------------------------------
  evaluateCredit: async (partyId, orderAmount, allowReservation = false) => {
    const response = await api.post(`/b2b/billing/credit/evaluate?partyId=${partyId}&amount=${orderAmount}&allowReservation=${allowReservation}`);
    return response.data;
  },

  createCreditOverride: async (data) => {
    const response = await api.post('/b2b/billing/credit/override', data);
    return response.data;
  },

  // What the client's credit terms say about a bill of this amount (shown on the registration screen before saving)
  getRegistrationGate: async (partyId, amount) => {
    const response = await api.post('/b2b/billing/registration-gate', null, { params: { partyId, amount } });
    return response.data;
  },

  getAdvances: async (partyId) => {
    const response = await api.get('/b2b/billing/advances', { params: { partyId } });
    return response.data || [];
  },

  // Order intake: what client systems sent in, and what staff do with it
  getInboundQueue: async (status) => {
    const response = await api.get('/b2b/inbound/queue', { params: { status: status || undefined } });
    return response.data || [];
  },
  processInboundOrder: async (trackingRef) => {
    const response = await api.post(`/b2b/inbound/${encodeURIComponent(trackingRef)}/process`);
    return response.data;
  },
  rejectInboundOrder: async (trackingRef, reason) => {
    const response = await api.post(`/b2b/inbound/${encodeURIComponent(trackingRef)}/reject`, { reason });
    return response.data;
  },

  // Keys that let a client's own system send orders (administrators)
  getApiClients: async (partyId) => {
    const response = await api.get('/b2b/api-clients', { params: { partyId } });
    return response.data || [];
  },
  createApiClient: async (data) => {
    const response = await api.post('/b2b/api-clients', data);
    return response.data;
  },
  rotateApiClient: async (id) => {
    const response = await api.post(`/b2b/api-clients/${id}/rotate`);
    return response.data;
  },
  revokeApiClient: async (id) => {
    const response = await api.post(`/b2b/api-clients/${id}/revoke`);
    return response.data;
  },

  getUnbilledOrders: async (partyId, fromDate, toDate, centreId) => {
    const response = await api.get('/b2b/billing/unbilled', {
      params: { partyId, fromDate, toDate, centreId }
    });
    return response.data || [];
  },

  generateBatchInvoice: async (data) => {
    const response = await api.post('/b2b/billing/generate-invoice', data);
    return response.data;
  },

  getInvoices: async (params = {}) => {
    const response = await api.get('/b2b/billing/invoices', { params });
    return response.data?.data || [];
  },

  getInvoiceDetails: async (invoiceId) => {
    const response = await api.get(`/b2b/billing/invoices/${invoiceId}`);
    return response.data?.data;
  },

  createAdvance: async (data) => {
    const response = await api.post('/b2b/billing/advances', data);
    return response.data;
  },

  allocatePayment: async (data) => {
    const response = await api.post('/b2b/billing/payments/allocate', data);
    return response.data;
  },

  getAgingReport: async (params = {}) => {
    const response = await api.get('/b2b/billing/aging-report', { params });
    return response.data?.data || [];
  },

  // Pre-Billing AI Anomaly Detection (REQ-B2B-13 - Advisory)
  getBillingAnomalies: async (partyId, fromDate, toDate) => {
    const response = await api.get(`/b2b/billing/anomalies/${partyId}`, {
      params: { fromDate, toDate }
    });
    return response.data?.data || [];
  },

  // ----------------------------------------------------
  // 3. Specimen Logistics (REQ-B2B-08)
  // ----------------------------------------------------
  getManifests: async (params = {}) => {
    const response = await api.get('/b2b/logistics/manifests', { params });
    if (Array.isArray(response.data)) return response.data;
    return response.data?.data || [];
  },

  getManifestDetails: async (manifestId) => {
    const response = await api.get(`/b2b/logistics/manifests/${manifestId}`);
    return response.data?.data !== undefined ? response.data.data : response.data;
  },

  createManifest: async (data) => {
    const response = await api.post('/b2b/logistics/manifests', data);
    return response.data?.data !== undefined ? response.data.data : response.data;
  },

  addManifestItem: async (manifestId, barcodeNo) => {
    const response = await api.post(`/b2b/logistics/manifests/${manifestId}/items`, { barcodeNo });
    return response.data?.data !== undefined ? response.data.data : response.data;
  },

  dispatchManifest: async (manifestId, data = {}) => {
    const response = await api.post(`/b2b/logistics/manifests/${manifestId}/dispatch`, data, { params: data });
    return response.data;
  },

  receiveManifest: async (manifestId, data = {}) => {
    const response = await api.post(`/b2b/logistics/manifests/${manifestId}/receive`, data, { params: data });
    return response.data;
  },

  verifyManifestItem: async (data) => {
    const response = await api.post('/b2b/logistics/manifests/items/verify', data);
    return response.data;
  },

  // ----------------------------------------------------
  // 4. External Code Mappings (REQ-B2B-07)
  // ----------------------------------------------------
  getCodeMappings: async (partyId) => {
    const response = await api.get(`/b2b/code-mappings/${partyId}`);
    return response.data?.data || [];
  },

  saveCodeMapping: async (data) => {
    const response = await api.post('/b2b/code-mappings', data);
    return response.data;
  },

  // ----------------------------------------------------
  // 5. B2B Customer Portal (REQ-B2B-09, 10, 11)
  // ----------------------------------------------------
  portalLogin: async (username, password) => {
    const response = await api.post('/b2b/portal/login', { username, password });
    return response.data;
  },

  portalLogout: async () => {
    try {
      await api.post('/b2b/portal/logout');
    } finally {
      sessionStorage.removeItem('b2b_portal_token');
      sessionStorage.removeItem('b2b_portal_user');
    }
  },

  getPortalSession: async () => {
    const response = await api.get('/b2b/portal/session');
    return response.data;
  },

  getPortalDashboard: async () => {
    const response = await api.get('/b2b/portal/dashboard');
    return response.data?.data ?? response.data;
  },

  getPortalOrders: async (params = {}) => {
    const response = await api.get('/b2b/portal/orders', { params });
    return response.data?.data ?? response.data ?? [];
  },

  getPortalReportDetails: async (ledgerTransactionNo) => {
    const response = await api.get(`/b2b/portal/reports/${ledgerTransactionNo}`);
    return response.data?.data ?? response.data;
  },

  // the client portal has its own invoice detail route; the staff one answers a portal login with 401
  getPortalInvoiceDetails: async (invoiceId) => {
    const response = await api.get(`/b2b/portal/invoices/${invoiceId}`);
    return response.data?.data ?? response.data;
  },

  getPortalInvoices: async () => {
    const response = await api.get('/b2b/portal/invoices');
    return response.data?.data ?? response.data ?? [];
  },

  getPortalLedgerStatement: async (params = {}) => {
    const response = await api.get('/b2b/portal/ledger-statement', { params });
    return response.data?.data ?? response.data ?? [];
  },

  getBrandingTemplate: async (partyId) => {
    const response = await api.get(`/b2b/portal/branding/${partyId}`);
    return response.data?.data;
  },

  saveBrandingTemplate: async (data) => {
    const response = await api.post('/b2b/portal/branding', data);
    return response.data;
  },

  uploadBrandingLogo: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/b2b/portal/branding/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // ----------------------------------------------------
  // 6. Portal Patient Self-Registration (Scoped & Tenant-Isolated)
  // ----------------------------------------------------
  searchPortalPatients: async (search) => {
    if (!search || search.trim().length < 3) return [];
    const response = await api.get('/b2b/portal/patients/search', { params: { search: search.trim() } });
    return response.data?.data ?? response.data ?? [];
  },

  getPortalAvailableTests: async (search = '') => {
    const params = search ? { search: search.trim() } : {};
    const response = await api.get('/b2b/portal/tests', { params });
    return response.data?.data ?? response.data ?? [];
  },

  bookPortalOrder: async (data) => {
    const response = await api.post('/b2b/portal/orders/book', data);
    return response.data;
  },

  bulkValidateOrders: async (rows) => {
    const response = await api.post('/b2b/portal/orders/bulk-validate', { rows });
    return response.data;
  },

  bulkBookOrders: async (batchName, rows) => {
    const response = await api.post('/b2b/portal/orders/bulk-book', { batchName, rows });
    return response.data;
  },

  // ----------------------------------------------------
  // 7. B2B Client Top-up Request & Lab Admin Approval Workflow
  // ----------------------------------------------------
  submitPortalTopup: async (data) => {
    const response = await api.post('/b2b/portal/topup/request', data);
    return response.data;
  },

  getPortalTopupHistory: async () => {
    const response = await api.get('/b2b/portal/topup/history');
    return response.data?.data ?? response.data ?? [];
  },

  getPendingTopups: async (partyId) => {
    const params = partyId ? { partyId } : {};
    const response = await api.get('/b2b/billing/topups/pending', { params });
    return response.data?.data ?? response.data ?? [];
  },

  approveTopup: async (requestId, remarks = '') => {
    const response = await api.post(`/b2b/billing/topups/${requestId}/approve`, { remarks });
    return response.data;
  },

  rejectTopup: async (requestId, reason) => {
    const response = await api.post(`/b2b/billing/topups/${requestId}/reject`, { reason });
    return response.data;
  }
};

export default b2bService;
